/**
 * F18 — Panel "Plan de pagos" (hitos de facturación parcial).
 * Reutilizable en la ficha de oportunidad (clientId + opportunityId) y en la ficha
 * de presupuesto (clientId + quoteId). Los hitos pendientes viven también
 * como eventos HITO_PAGO en el calendario.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { currentLocaleTag } from '../../lib/formatting'
import {
  AlertCircle,
  CalendarClock,
  Check,
  Loader2,
  Pencil,
  Plus,
  Receipt,
  Repeat,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog } from '../../lib/dialogs'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../../lib/money'
import { addMonths, toDateInput } from '../../lib/datetime'
import { useTableSort } from '../../lib/useTableSort'
import Modal from '../ui/Modal'
import SuggestMilestonesModal from './SuggestMilestonesModal'
import {
  RecurringInvoicePeriodicitySchema,
  type MilestoneDto,
  type MilestoneItemInput,
  type MilestoneListResponse,
  type RecurringInvoicePeriodicityT,
} from '@jr2os/shared'

const inputCls =
  'w-full px-2 py-1 rounded bg-[var(--bg-input-soft)] border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-jr2-500/50'

interface Props {
  clientId: string
  opportunityId?: string
  quoteId?: string
  canWrite: boolean
  /**
   * ¿Sugerir HITOS con IA? Los hitos son el calendario de pagos de un PROYECTO,
   * así que el botón de IA solo aparece cuando la oportunidad ya es proyecto
   * (ganada / en ejecución). Mientras es solo una oportunidad, la IA sugiere
   * LÍNEAS de facturación desde la ficha (Resumen), no hitos. Default: false.
   */
  isProject?: boolean
}

// L6 #56/#90 — Convierte un % de IVA escrito por el usuario ('21', '21,5', '21.5')
// a fracción string ('0.21', '0.215'). Tolera la coma decimal (locale ES) — antes
// `Number('21,5')` daba NaN y degradaba a 0% en silencio. Fuera de rango → 21%.
function vatPctToFraction(raw: string): string {
  const n = Number(String(raw).replace(',', '.'))
  if (!Number.isFinite(n) || n < 0 || n > 100) return '0.21'
  return String(n / 100)
}

export default function MilestonesPanel({
  clientId,
  opportunityId,
  quoteId,
  canWrite,
  isProject = false,
}: Props) {
  const { t } = useTranslation(['billing', 'common'])
  const navigate = useNavigate()
  const [data, setData] = useState<MilestoneListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [suggesting, setSuggesting] = useState(false)
  // M6 — hito que se está convirtiendo en cuota recurrente.
  const [converting, setConverting] = useState<MilestoneDto | null>(null)

  // Form de alta/edición de un hito.
  const [fTitle, setFTitle] = useState('')
  const [fAmountEur, setFAmountEur] = useState('')
  const [fVatPct, setFVatPct] = useState('21')
  const [fDate, setFDate] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      const params = new URLSearchParams()
      if (quoteId) params.set('quoteId', quoteId)
      else if (opportunityId) params.set('opportunityId', opportunityId)
      else params.set('clientId', clientId)
      setData(await api.get<MilestoneListResponse>(`/milestones?${params.toString()}`))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('milestones.errors.load'))
    }
  }, [clientId, opportunityId, quoteId])

  useEffect(() => {
    void load()
  }, [load])

  const pending = useMemo(
    () => (data?.items ?? []).filter((m) => m.status === 'PENDIENTE'),
    [data]
  )

  const toggle = (id: string) => {
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const resetForm = () => {
    setFTitle('')
    setFAmountEur('')
    setFVatPct('21')
    setFDate('')
    setAdding(false)
    setEditingId(null)
  }

  const startEdit = (m: MilestoneDto) => {
    setEditingId(m.id)
    setAdding(false)
    setFTitle(m.title)
    setFAmountEur(centsToEurosInput(m.amountCents))
    setFVatPct((Number(m.vatRate) * 100).toString())
    setFDate(m.dueDate.slice(0, 10))
  }

  const submitForm = async () => {
    if (!fTitle.trim() || !fDate || parseEurosToCents(fAmountEur) <= 0) {
      setError(t('milestones.errors.invalidForm'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (editingId) {
        await api.patch(`/milestones/${editingId}`, {
          title: fTitle.trim(),
          amountCents: parseEurosToCents(fAmountEur),
          vatRate: vatPctToFraction(fVatPct),
          dueDate: new Date(`${fDate}T12:00:00`).toISOString(),
        })
      } else {
        await api.post('/milestones', {
          clientId,
          opportunityId: opportunityId ?? null,
          quoteId: quoteId ?? null,
          items: [
            {
              position: data?.items.length ?? 0,
              title: fTitle.trim(),
              amountCents: parseEurosToCents(fAmountEur),
              vatRate: vatPctToFraction(fVatPct),
              dueDate: new Date(`${fDate}T12:00:00`).toISOString(),
            },
          ],
        })
      }
      resetForm()
      await load()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('milestones.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async (id: string) => {
    if (!(await confirmDialog({ message: t('milestones.confirmDelete'), tone: 'danger' }))) return
    setBusy(true)
    try {
      await api.delete(`/milestones/${id}`)
      setSelected((s) => {
        const next = new Set(s)
        next.delete(id)
        return next
      })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('milestones.errors.generic'))
    } finally {
      setBusy(false)
    }
  }

  // Un hito FACTURADO sigue siendo editable mientras su factura es un BORRADOR
  // (aún no emitida al cliente): descartamos ese borrador — el backend devuelve
  // todos sus hitos a PENDIENTE y reaviva sus eventos — y vuelven a ser editables.
  // Nunca se ofrece para facturas emitidas (EMITIDA/PARCIAL/COBRADA): integridad fiscal.
  const onRevertDraft = async (m: MilestoneDto) => {
    if (!m.invoiceId) return
    if (!(await confirmDialog({ message: t('milestones.confirmRevertDraft'), tone: 'danger' })))
      return
    setBusy(true)
    setError(null)
    try {
      await api.delete(`/invoices/${m.invoiceId}`)
      await load()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('milestones.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const onInvoiceSelection = async () => {
    setBusy(true)
    setError(null)
    try {
      const { invoiceId } = await api.post<{ invoiceId: string }>('/milestones/invoice', {
        ids: [...selected],
      })
      navigate(`/billing/${invoiceId}`)
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('milestones.errors.generic')))
      setBusy(false)
    }
  }

  const items = data?.items ?? []

  // Orden por columna (panel dentro de otra ficha: solo en memoria). Sin orden elegido, el de siempre (fecha de vencimiento).
  const { rows: milestoneRows, header } = useTableSort(
    data?.items,
    {
      milestone: (m) => m.title,
      date: (m) => m.dueDate,
      base: (m) => m.amountCents,
      vatPct: (m) => Number(m.vatRate),
      status: (m) => t('enums:MilestoneStatus.' + m.status),
    },
    { persist: 'state' }
  )

  return (
    <div className="surface p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
          <CalendarClock size={15} className="text-teal-700 dark:text-teal-300" />
          {t('milestones.title')}
          {data && data.pendingAmountCents > 0 && (
            <span className="text-xs t-muted font-normal">
              {`· ${t('milestones.pending')} ${formatCents(data.pendingAmountCents)} ${t('milestones.baseSuffix')}`}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {canWrite && selected.size > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onInvoiceSelection()}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Receipt size={13} />}
              {t('milestones.invoiceSelection', { count: selected.size })}
            </button>
          )}
          {canWrite && Boolean(opportunityId) && isProject && (
            <button
              type="button"
              onClick={() => setSuggesting(true)}
              className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <Sparkles size={13} /> {t('milestones.suggestAi')}
            </button>
          )}
          {canWrite && !adding && !editingId && (
            <button
              type="button"
              onClick={() => {
                resetForm()
                setAdding(true)
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <Plus size={13} /> {t('milestones.addMilestone')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {items.length === 0 && !adding && (
        <p className="text-xs t-faint">
          {t('milestones.emptyHint')}
        </p>
      )}

      {(items.length > 0 || adding) && (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider t-faint">
            <tr>
              {canWrite && <th className="w-7" />}
              {header('milestone', t('milestones.col.milestone'), { className: 'py-1' })}
              {header('date', t('milestones.col.date'), { className: 'w-28' })}
              {header('base', t('milestones.col.base'), { align: 'right', className: 'w-28' })}
              {header('vatPct', t('milestones.col.vatPct'), { align: 'right', className: 'w-16' })}
              {header('status', t('milestones.col.status'), { className: 'w-28' })}
              {canWrite && <th className="w-16" />}
            </tr>
          </thead>
          <tbody>
            {milestoneRows.map((m) =>
              editingId === m.id ? (
                <FormRow
                  key={m.id}
                  canSelect={canWrite}
                  fTitle={fTitle}
                  fAmountEur={fAmountEur}
                  fVatPct={fVatPct}
                  fDate={fDate}
                  busy={busy}
                  setFTitle={setFTitle}
                  setFAmountEur={setFAmountEur}
                  setFVatPct={setFVatPct}
                  setFDate={setFDate}
                  onSubmit={() => void submitForm()}
                  onCancel={resetForm}
                />
              ) : (
                <tr key={m.id} className="border-t border-base/40">
                  {canWrite && (
                    <td className="py-1.5">
                      {m.status === 'PENDIENTE' && (
                        <input
                          type="checkbox"
                          checked={selected.has(m.id)}
                          onChange={() => toggle(m.id)}
                        />
                      )}
                    </td>
                  )}
                  <td className="py-1.5 t-primary">
                    {m.title}
                    {m.description && (
                      <span className="block text-xs t-faint truncate max-w-[280px]">
                        {m.description}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-xs t-secondary">
                    {new Date(m.dueDate).toLocaleDateString(currentLocaleTag())}
                  </td>
                  <td className="py-1.5 text-right font-mono t-secondary">
                    {formatCents(m.amountCents)}
                  </td>
                  <td className="py-1.5 text-right text-xs t-muted">
                    {(Number(m.vatRate) * 100).toFixed(0)}
                  </td>
                  <td className="py-1.5 text-xs">
                    {m.status === 'FACTURADO' && m.invoiceId ? (
                      <Link
                        to={`/billing/${m.invoiceId}`}
                        className="text-jr2-700 dark:text-jr2-300 hover:underline"
                      >
                        {m.invoiceNumber ?? t('milestones.draftInvoice')}
                      </Link>
                    ) : (
                      <span
                        className={
                          m.status === 'PENDIENTE' ? 'text-teal-700 dark:text-teal-300' : 't-muted'
                        }
                      >
                        {t('enums:MilestoneStatus.' + m.status)}
                      </span>
                    )}
                  </td>
                  {canWrite && (
                    <td className="py-1.5 text-right whitespace-nowrap">
                      {m.status === 'PENDIENTE' ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setConverting(m)}
                            className="t-faint hover:text-teal-700 dark:hover:text-teal-300 mr-2"
                            title={t('milestones.convertMilestone')}
                          >
                            <Repeat size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => startEdit(m)}
                            className="t-faint hover:text-jr2-700 dark:hover:text-jr2-200 mr-2"
                            title={t('milestones.editMilestone')}
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => void onDelete(m.id)}
                            className="t-faint hover:text-rose-700 dark:hover:text-rose-300"
                            title={t('milestones.deleteMilestone')}
                          >
                            <Trash2 size={13} />
                          </button>
                        </>
                      ) : (
                        m.invoiceStatus === 'BORRADOR' && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void onRevertDraft(m)}
                            className="t-faint hover:text-amber-700 dark:hover:text-amber-300"
                            title={t('milestones.revertDraft')}
                          >
                            <Undo2 size={13} />
                          </button>
                        )
                      )}
                    </td>
                  )}
                </tr>
              )
            )}
            {adding && (
              <FormRow
                canSelect={canWrite}
                fTitle={fTitle}
                fAmountEur={fAmountEur}
                fVatPct={fVatPct}
                fDate={fDate}
                busy={busy}
                setFTitle={setFTitle}
                setFAmountEur={setFAmountEur}
                setFVatPct={setFVatPct}
                setFDate={setFDate}
                onSubmit={() => void submitForm()}
                onCancel={resetForm}
              />
            )}
          </tbody>
        </table>
      )}

      {opportunityId && isProject && (
        <SuggestMilestonesModal
          open={suggesting}
          onClose={() => setSuggesting(false)}
          clientId={clientId}
          opportunityId={opportunityId}
          onCreated={() => {
            void load()
          }}
        />
      )}

      {converting && (
        <ConvertToRecurringModal
          milestone={converting}
          onClose={() => setConverting(null)}
          onConverted={() => {
            setConverting(null)
            void load()
          }}
        />
      )}
    </div>
  )
}

/**
 * M6 — Convertir un hito PENDIENTE en cuota recurrente con periodicidad y
 * fecha límite de contrato. Sin límite (ni nº de cuotas ni fecha fin) la
 * cuota se renueva indefinidamente → alerta de renovación en las fichas.
 */
function ConvertToRecurringModal({
  milestone,
  onClose,
  onConverted,
}: {
  milestone: MilestoneDto
  onClose: () => void
  onConverted: () => void
}) {
  const { t } = useTranslation(['billing', 'common', 'enums'])
  const dueDay = Math.min(28, new Date(milestone.dueDate).getUTCDate())
  const [periodicity, setPeriodicity] = useState<RecurringInvoicePeriodicityT>('MONTHLY')
  const [dayOfMonth, setDayOfMonth] = useState(String(dueDay))
  const [startDate, setStartDate] = useState(milestone.dueDate.slice(0, 10))
  const [maxOccurrences, setMaxOccurrences] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const noLimit = !maxOccurrences.trim() && !endsAt

  const onSubmit = async () => {
    setBusy(true)
    setError(null)
    try {
      const count = Number(maxOccurrences)
      await api.post(`/milestones/${milestone.id}/convert-to-recurring`, {
        periodicity,
        dayOfMonth: Number(dayOfMonth) || dueDay,
        startDate: startDate || null,
        maxOccurrences:
          maxOccurrences.trim() && Number.isFinite(count) && count > 0 ? Math.floor(count) : null,
        endsAt: endsAt || null,
      })
      onConverted()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('milestones.errors.generic')))
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('milestones.convert.title')} maxWidth="max-w-lg">
      <div className="space-y-3">
        <p className="text-sm t-secondary">
          {t('milestones.convert.intro', {
            title: milestone.title,
            amount: formatCents(milestone.amountCents),
          })}
        </p>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">
            <label className="block text-xs t-muted mb-1">{t('milestones.convert.periodicity')}</label>
            <select
              value={periodicity}
              onChange={(e) => setPeriodicity(e.target.value as RecurringInvoicePeriodicityT)}
              className={inputCls}
            >
              {RecurringInvoicePeriodicitySchema.options.map((p) => (
                <option key={p} value={p}>
                  {t(`enums:RecurringInvoicePeriodicity.${p}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1">{t('milestones.convert.dayOfMonth')}</label>
            <input
              type="text"
              inputMode="numeric"
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1">{t('milestones.convert.firstDate')}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1">
              {t('milestones.convert.maxOccurrences')}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={maxOccurrences}
              onChange={(e) => setMaxOccurrences(e.target.value)}
              placeholder={t('milestones.convert.noLimitPlaceholder')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1">{t('milestones.convert.endsAt')}</label>
            <input
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        {noLimit && (
          <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
            <AlertCircle size={12} /> {t('milestones.convert.noLimitHint')}
          </p>
        )}

        {error && (
          <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
            <AlertCircle size={12} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void onSubmit()}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Repeat size={14} />}
            {t('milestones.convert.submit')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function FormRow(props: {
  canSelect: boolean
  fTitle: string
  fAmountEur: string
  fVatPct: string
  fDate: string
  busy: boolean
  setFTitle: (v: string) => void
  setFAmountEur: (v: string) => void
  setFVatPct: (v: string) => void
  setFDate: (v: string) => void
  onSubmit: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation('billing')
  return (
    <tr className="border-t border-base/40 bg-[var(--bg-input-soft)]">
      {props.canSelect && <td />}
      <td className="py-1.5 pr-2">
        <input
          value={props.fTitle}
          onChange={(e) => props.setFTitle(e.target.value)}
          placeholder={t('milestones.form.titlePlaceholder')}
          className={inputCls}
          autoFocus
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          type="date"
          value={props.fDate}
          onChange={(e) => props.setFDate(e.target.value)}
          className={inputCls}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          value={props.fAmountEur}
          onChange={(e) => props.setFAmountEur(e.target.value)}
          placeholder="0,00"
          className={`${inputCls} text-right`}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          value={props.fVatPct}
          onChange={(e) => props.setFVatPct(e.target.value)}
          className={`${inputCls} text-right`}
        />
      </td>
      <td colSpan={2} className="py-1.5 whitespace-nowrap">
        <button
          type="button"
          disabled={props.busy}
          onClick={props.onSubmit}
          className="px-2 py-1 rounded text-xs border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10 mr-1.5"
        >
          {props.busy ? <Loader2 size={12} className="animate-spin inline" /> : <Check size={12} className="inline" />}
        </button>
        <button
          type="button"
          onClick={props.onCancel}
          className="px-2 py-1 rounded text-xs border border-base t-muted hover:bg-hover"
        >
          <X size={12} className="inline" />
        </button>
      </td>
    </tr>
  )
}

/**
 * Modal "Generar plan de pagos" desde un presupuesto: reparte la base
 * imponible en N hitos mensuales editables y los crea de una vez.
 */
export function PaymentPlanModal({
  open,
  onClose,
  clientId,
  opportunityId,
  quoteId,
  subtotalCents,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  clientId: string
  opportunityId: string | null
  quoteId: string
  subtotalCents: number
  onCreated: () => void
}) {
  const { t } = useTranslation(['billing', 'common'])
  const [rows, setRows] = useState<Array<{ title: string; amountEur: string; date: string }>>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const buildRows = useCallback(
    (n: number) => {
      const base = Math.floor(subtotalCents / n)
      const remainder = subtotalCents - base * n
      const out: Array<{ title: string; amountEur: string; date: string }> = []
      const today = new Date()
      for (let i = 0; i < n; i++) {
        // addMonths con clamp: un plan generado el 31 no se salta febrero.
        const d = addMonths(today, i)
        out.push({
          title: t('paymentPlan.rowTitle', { i: i + 1, n }),
          amountEur: centsToEurosInput(base + (i === n - 1 ? remainder : 0)),
          date: toDateInput(d),
        })
      }
      setRows(out)
    },
    [subtotalCents]
  )

  useEffect(() => {
    if (open) buildRows(3)
  }, [open, buildRows])

  const sumCents = rows.reduce((acc, r) => acc + parseEurosToCents(r.amountEur), 0)

  const onSubmit = async () => {
    setBusy(true)
    setError(null)
    try {
      const items: MilestoneItemInput[] = rows.map((r, i) => ({
        position: i,
        title: r.title.trim() || t('paymentPlan.fallbackTitle', { i: i + 1 }),
        amountCents: parseEurosToCents(r.amountEur),
        vatRate: '0.21',
        dueDate: new Date(`${r.date}T12:00:00`).toISOString(),
      }))
      await api.post('/milestones', { clientId, opportunityId, quoteId, items })
      onCreated()
      onClose()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('milestones.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t('paymentPlan.title')}>
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm t-secondary">
          {t('paymentPlan.splitPrefix', { amount: formatCents(subtotalCents) })}
          <select
            onChange={(e) => buildRows(Number(e.target.value))}
            defaultValue="3"
            className="bg-transparent border border-base rounded px-2 py-1 text-sm"
          >
            {[2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          {t('paymentPlan.splitSuffix')}
        </div>

        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="text-left py-1">{t('milestones.col.milestone')}</th>
              <th className="text-left w-32">{t('milestones.col.date')}</th>
              <th className="text-right w-28">{t('paymentPlan.col.baseEur')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-base/40">
                <td className="py-1 pr-2">
                  <input
                    value={r.title}
                    onChange={(e) =>
                      setRows((rs) => rs.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))
                    }
                    className={inputCls}
                  />
                </td>
                <td className="py-1 pr-2">
                  <input
                    type="date"
                    value={r.date}
                    onChange={(e) =>
                      setRows((rs) => rs.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))
                    }
                    className={inputCls}
                  />
                </td>
                <td className="py-1">
                  <input
                    value={r.amountEur}
                    onChange={(e) =>
                      setRows((rs) =>
                        rs.map((x, j) => (j === i ? { ...x, amountEur: e.target.value } : x))
                      )
                    }
                    className={`${inputCls} text-right`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className={`text-xs ${sumCents === subtotalCents ? 't-faint' : 'text-amber-700 dark:text-amber-300'}`}>
          {t('paymentPlan.sum', { amount: formatCents(sumCents) })}
          {sumCents !== subtotalCents &&
            ` ${t('paymentPlan.differs', { amount: formatCents(subtotalCents) })}`}
        </p>

        {error && (
          <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
            <AlertCircle size={12} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={busy || rows.length === 0}
            onClick={() => void onSubmit()}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
            {t('paymentPlan.create')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
